import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

interface BreadcrumbContextValue {
  /** Set a dynamic label for a route path (e.g. '/tenants/abc123' → 'Saguaro Valley FD') */
  setBreadcrumbLabel: (path: string, label: string) => void;
  /** Get the dynamic label for a path, or undefined if not set */
  getBreadcrumbLabel: (path: string) => string | undefined;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [labels, setLabels] = useState<Record<string, string>>({});

  const setBreadcrumbLabel = useCallback((path: string, label: string) => {
    setLabels((prev) => {
      if (prev[path] === label) return prev;
      return { ...prev, [path]: label };
    });
  }, []);

  const getBreadcrumbLabel = useCallback(
    (path: string) => labels[path],
    [labels],
  );

  const value = useMemo(
    () => ({ setBreadcrumbLabel, getBreadcrumbLabel }),
    [setBreadcrumbLabel, getBreadcrumbLabel],
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbs() {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) throw new Error('useBreadcrumbs must be used within BreadcrumbProvider');
  return ctx;
}
